import { useMemo } from 'react';
import { ScriptMode, IEpisodeScript } from '@packages/global';

/**
 * 模式检测结果
 */
export interface ScriptModeInfo {
  /** 当前模式 */
  mode: ScriptMode;
  /** 是否有剧本内容（FILE_SCRIPT 或 PROJECT_SCRIPT 模式） */
  hasScriptContent: boolean;
  /** 是否为自由创作模式（PROMPT） */
  isPromptMode: boolean;
  /** 上传的原始脚本内容 */
  uploadedScriptContent?: string;
  /** 模式描述 */
  modeDescription: string;
  /** 操作动词（生成/提取） */
  actionVerb: string;
}

/**
 * 脚本模式 Hook
 * 
 * 根据脚本数据自动检测模式
 * - PROMPT: AI 根据用户需求自由创作
 * - FILE_SCRIPT: 基于上传的脚本文件提取内容
 * - PROJECT_SCRIPT: 基于项目剧本章节提取内容
 * 
 * Requirements: 13.4, 13.5, 13.6
 */
export function useScriptMode(script: IEpisodeScript | null): ScriptModeInfo {
  return useMemo(() => {
    // 默认为 PROMPT 模式
    if (!script) {
      return {
        mode: ScriptMode.PROMPT,
        hasScriptContent: false,
        isPromptMode: true,
        modeDescription: 'AI 根据您的需求自由创作',
        actionVerb: '生成',
      };
    }

    // 检测模式
    const mode = script.scriptMode || ScriptMode.PROMPT;
    const hasScriptContent = mode === ScriptMode.FILE_SCRIPT || mode === ScriptMode.PROJECT_SCRIPT;
    const isPromptMode = mode === ScriptMode.PROMPT;

    // 根据模式返回相应的描述
    let modeDescription: string;
    switch (mode) {
      case ScriptMode.FILE_SCRIPT:
        modeDescription = '基于上传的脚本文件提取内容';
        break;
      case ScriptMode.PROJECT_SCRIPT:
        modeDescription = '基于项目剧本章节提取内容';
        break;
      case ScriptMode.MV:
        modeDescription = '基于音频节奏生成 MV 分镜';
        break;
      default:
        modeDescription = 'AI 根据您的需求自由创作';
    }

    return {
      mode,
      hasScriptContent,
      isPromptMode,
      uploadedScriptContent: script.uploadedScriptContent,
      modeDescription,
      actionVerb: hasScriptContent ? '提取' : '生成',
    };
  }, [script]);
}

/**
 * 检测脚本模式（纯函数版本）
 * 用于不需要 React hook 的场景
 */
export function detectScriptMode(script: IEpisodeScript | null): ScriptMode {
  if (!script) return ScriptMode.PROMPT;

  // 优先使用明确的 scriptMode 字段
  if (script.scriptMode) return script.scriptMode;

  // 检测是否有上传的脚本内容
  if (script.uploadedScriptContent) {
    return ScriptMode.FILE_SCRIPT;
  }

  return ScriptMode.PROMPT;
}

/**
 * 获取模式相关的 UI 文本
 */
export function getModeTexts(mode: ScriptMode) {
  // FILE_SCRIPT 和 PROJECT_SCRIPT 模式都使用"提取"
  const hasScriptContent = mode === ScriptMode.FILE_SCRIPT || mode === ScriptMode.PROJECT_SCRIPT;

  return {
    // 操作动词
    actionVerb: hasScriptContent ? '提取' : '生成',

    // 按钮文本
    regenerateButton: hasScriptContent ? '重新提取' : '重新生成',
    generateButton: hasScriptContent ? '提取分镜' : '生成分镜',

    // 步骤标题
    stepTitle: (stepName: string) => hasScriptContent ? `提取${stepName}` : `生成${stepName}`,

    // 提示文本
    emptyHint: hasScriptContent
      ? '请上传脚本文件以开始提取'
      : '请输入您的创作需求',

    // 进度文本
    progressText: (current: number, total: number) =>
      hasScriptContent
        ? `已提取 ${current}/${total} 步骤`
        : `已完成 ${current}/${total} 步骤`,
  };
}

export type UseScriptModeReturn = ReturnType<typeof useScriptMode>;
